import { OrTableProps } from "./or-table.type";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import { GlobalTableStyles } from "../../../theme/global-styles";
import { TableCellRenderer } from "./components/table-cell-renderer";
import { cn } from "../../../utils/cn";
import { TABLE_CONFIGS } from "./or-table-config.const";
import { MlTableMessage } from "../../molecules";

export const OrTable = ({
  tableType,
  data,
  loading = false,
  rowsPerPage = 6,
  page = 0,
  totalRows,
  className = "",
  "data-testid": testId,
  searchParams = {},
  emptyStateConfig,
  noResultsConfig,
  onPageChange,
  onRowClick,
  isMini = false,
}: OrTableProps) => {
  const { headers } = TABLE_CONFIGS[tableType];

  const renderTableRows = () => {
    if (loading) {
      return (
        <TableRow>
          <td colSpan={headers.length}>
            <div className="flex justify-center items-center h-full absolute top-0 left-0 w-full">
              <CircularProgress className="!text-primary-500" />
            </div>
          </td>
        </TableRow>
      );
    }

    if (data.length === 0) {
      return (
        <TableRow>
          <td colSpan={headers.length}>
            <div className="flex justify-center items-center h-full absolute top-0 left-0 w-full">
              <MlTableMessage
                title={noResultsConfig?.title || ""}
                description={noResultsConfig?.description || ""}
                showActionButton={noResultsConfig?.showActionButton}
                actionButtonText={noResultsConfig?.actionButtonText}
                onActionClick={noResultsConfig?.onActionClick}
              />
            </div>
          </td>
        </TableRow>
      );
    }

    return data.map((row) => (
      <TableRow
        data-testid={testId}
        key={row.id}
        onClick={() => onRowClick?.(row)}
        className={cn(
          row.className,
          onRowClick && "cursor-pointer hover:bg-primary-50",
        )}
      >
        {row.cells?.map((cell, cellIndex) => (
          <TableCellRenderer key={`${row.id}-${cellIndex}`} cell={cell} />
        ))}
      </TableRow>
    ));
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    onPageChange?.(newPage);
  };

  const shouldShowEmptyState =
    (!searchParams || Object.keys(searchParams).length === 0) &&
    data.length === 0 &&
    !loading;

  if (shouldShowEmptyState) {
    return (
      <MlTableMessage
        title={emptyStateConfig?.title || ""}
        description={emptyStateConfig?.description || <></>}
        showActionButton={emptyStateConfig?.showActionButton || false}
        actionButtonText={emptyStateConfig?.actionButtonText}
        onActionClick={emptyStateConfig?.onActionClick}
      />
    );
  }

  const labelDisplayedRows = () => {
    if (isMini) {
      return `${page * rowsPerPage + 1} - ${Math.min(page * rowsPerPage + rowsPerPage, totalRows)} de ${totalRows}`;
    }

    return `Mostrando los resultados ${page * rowsPerPage + 1} - ${Math.min(page * rowsPerPage + rowsPerPage, totalRows)} de ${totalRows} en total`;
  };

  return (
    <>
      <GlobalTableStyles
        loading={loading}
        noResults={data.length === 0}
        whiteHeader={isMini}
      />
      <div className={`relative min-h-[472px] flex flex-col ${className}`}>
        <TableContainer
          component="div"
          sx={{
            overflow: "hidden",
            flex: 1,
          }}
        >
          <Table className="!border-none">
            <TableHead>
              <TableRow>
                {headers.map((headerObj, index) => (
                  <TableCell key={index}>
                    <span>{headerObj.header}</span>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{renderTableRows()}</TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          className={cn(isMini ? "!border-t !border-surface-200" : "")}
          component="div"
          count={totalRows}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={labelDisplayedRows}
          showFirstButton
          showLastButton
        />
      </div>
    </>
  );
};
